import React from 'react';
import { Card, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { PATH_EVENTS } from 'routes/Paths';

const TYPE_PALETTE = [
	{ color: '#1d9eb0', bg: 'rgba(29,158,176,0.1)', icon: 'fe-radio' },
	{ color: '#F68521', bg: 'rgba(246,133,33,0.1)', icon: 'fe-activity' },
	{ color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)', icon: 'fe-star' },
	{ color: '#10b981', bg: 'rgba(16,185,129,0.1)', icon: 'fe-zap' },
	{ color: '#ef4444', bg: 'rgba(239,68,68,0.1)',   icon: 'fe-heart' },
	{ color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', icon: 'fe-award' },
];

const getTypeMeta = (eventType) => {
	if (!eventType) return TYPE_PALETTE[0];
	let hash = 0;
	for (let i = 0; i < eventType.length; i++) hash = eventType.charCodeAt(i) + ((hash << 5) - hash);
	return TYPE_PALETTE[Math.abs(hash) % TYPE_PALETTE.length];
};

const formatTypeLabel = (eventType) => {
	if (!eventType) return 'Event';
	return eventType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const parseSpeakers = (raw) => {
	if (!raw) return null;
	try {
		const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
		return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
	} catch { return null; }
};

const formatPrice = (price) => {
	if (!price || price === 0) return null;
	return `Ksh ${price.toLocaleString()}`;
};

const EventCard = ({ event, isRegistered }) => {
	const meta = getTypeMeta(event.eventType);
	const sessionDate = moment(event.sessionDatetime);
	const isUpcoming = sessionDate.isAfter(moment());
	const isFull = event.maxParticipants > 0 && event.registrationCount >= event.maxParticipants;
	const spotsLeft = event.maxParticipants > 0 ? event.maxParticipants - event.registrationCount : null;

	const speakers = parseSpeakers(event.speakersData) ||
		(event.speakerName ? [{ name: event.speakerName, title: event.speakerTitle }] : []);

	const virtualPriceLabel = formatPrice(event.price);
	const physicalPriceLabel = formatPrice(event.physicalPrice);

	return (
		<Card className="event-card h-100">
			{/* Poster — full width at top, portrait ratio, full image visible */}
			<div className="event-poster-top">
				{event.bannerImage ? (
					<img src={event.bannerImage} alt={event.title} className="event-poster-img" />
				) : (
					<div className="event-poster-empty" style={{ background: meta.bg }}>
						<i className={`fe ${meta.icon}`} style={{ color: meta.color, fontSize: '2.5rem', opacity: 0.5 }}></i>
					</div>
				)}
				{/* Status pill overlaid on poster */}
				{isUpcoming ? (
					<span className="event-poster-pill event-poster-pill-upcoming">Upcoming</span>
				) : (
					<span className="event-poster-pill event-poster-pill-past">Past</span>
				)}
			</div>

			{/* Colour accent bar */}
			<div className="event-accent-bar" style={{ background: meta.color }} />

			<Card.Body className="p-3 d-flex flex-column">
				{/* Type + price badges */}
				<div className="d-flex align-items-center flex-wrap gap-1 mb-2">
					<Badge bg="" className="event-type-badge" style={{ background: meta.bg, color: meta.color }}>
						<i className={`fe ${meta.icon} me-1`}></i>
						{formatTypeLabel(event.eventType)}
					</Badge>
					{virtualPriceLabel && (
						<Badge bg="" className="event-price-badge" title="Virtual">
							<i className="fe fe-monitor me-1"></i>{virtualPriceLabel}
						</Badge>
					)}
					{physicalPriceLabel && (
						<Badge bg="" className="event-price-badge event-price-physical" title="In-Person">
							<i className="fe fe-map-pin me-1"></i>{physicalPriceLabel}
						</Badge>
					)}
				</div>

				{/* Title */}
				<h5 className="event-title mb-2">{event.title}</h5>

				{/* Description */}
				{event.description && (
					<p className="event-description text-muted mb-2">{event.description}</p>
				)}

				{/* Speakers */}
				{speakers.length > 0 && (
					<div className="event-speakers mb-2">
						{speakers.map((s, i) => (
							<div key={i} className="event-speaker">
								<i className="fe fe-mic text-muted me-2"></i>
								<span className="speaker-name">{s.name}</span>
								{s.title && <span className="speaker-title"> — {s.title}</span>}
							</div>
						))}
					</div>
				)}

				{/* Date / Time */}
				<div className="event-meta-list mb-3">
					<div className="event-meta-item">
						<i className="fe fe-calendar me-2 text-muted"></i>
						<span>{sessionDate.format('ddd, MMM D, YYYY')}</span>
					</div>
					<div className="event-meta-item">
						<i className="fe fe-clock me-2 text-muted"></i>
						<span>{sessionDate.format('h:mm A')}</span>
						{event.durationMinutes > 0 && (
							<span className="text-muted ms-1">({event.durationMinutes} min)</span>
						)}
					</div>
					{spotsLeft !== null && isUpcoming && (
						<div className="event-meta-item">
							<i className="fe fe-users me-2 text-muted"></i>
							<span className={spotsLeft <= 5 ? 'text-danger fw-semibold' : ''}>
								{isFull ? 'Fully booked' : `${spotsLeft} spot${spotsLeft !== 1 ? 's' : ''} left`}
							</span>
						</div>
					)}
					{event.eventType === 'physical_event' && event.venueLocation && (
						<div className="event-meta-item">
							<i className="fe fe-map-pin me-2 text-muted"></i>
							<span>{event.venueLocation}</span>
						</div>
					)}
				</div>

				{/* CTA */}
				<div className="mt-auto">
					{isRegistered && (
						<div className="event-registered-badge mb-2">
							<i className="fe fe-check-circle me-2"></i>You're registered
						</div>
					)}
					{!isRegistered && isFull && (
						<div className="event-full-badge mb-2">
							<i className="fe fe-slash me-2"></i>Fully Booked
						</div>
					)}
					{!isUpcoming && !isRegistered && (
						<div className="event-ended-badge mb-2">
							<i className="fe fe-calendar me-2"></i>Session Ended
						</div>
					)}
					<Link
						to={PATH_EVENTS.detail(event.guid)}
						className="event-details-btn d-flex align-items-center justify-content-center w-100"
					>
						<i className="fe fe-arrow-right me-2"></i>
						{isRegistered ? 'View Event' : isUpcoming && !isFull ? 'View & Register' : 'View Details'}
					</Link>
				</div>
			</Card.Body>

			<style>{`
				.event-card {
					border: 1px solid #e2e8f0;
					border-radius: 16px;
					box-shadow: 0 2px 8px rgba(0,0,0,0.05);
					transition: transform 0.25s ease, box-shadow 0.25s ease;
					overflow: hidden;
				}
				.event-card:hover {
					transform: translateY(-4px);
					box-shadow: 0 8px 24px rgba(0,0,0,0.1);
				}

				/* Poster at top — portrait ratio, full image shown */
				.event-poster-top {
					position: relative;
					width: 100%;
					aspect-ratio: 2/3;
					background: #0f172a;
					overflow: hidden;
				}
				.event-poster-img {
					width: 100%;
					height: 100%;
					object-fit: contain;
					display: block;
				}
				.event-poster-empty {
					width: 100%;
					height: 100%;
					display: flex;
					align-items: center;
					justify-content: center;
				}
				.event-poster-pill {
					position: absolute;
					top: 10px;
					right: 10px;
					font-size: 0.625rem;
					font-weight: 800;
					padding: 3px 10px;
					border-radius: 20px;
					text-transform: uppercase;
					letter-spacing: 0.5px;
				}
				.event-poster-pill-upcoming { background: rgba(16,185,129,0.9); color: white; }
				.event-poster-pill-past { background: rgba(100,116,139,0.8); color: white; }

				.event-accent-bar { height: 3px; width: 100%; }

				/* Badges */
				.event-type-badge {
					font-size: 0.6875rem;
					font-weight: 700;
					padding: 4px 10px;
					border-radius: 8px;
					display: inline-flex;
					align-items: center;
				}
				.event-price-badge {
					background: rgba(16,185,129,0.1) !important;
					color: #10b981 !important;
					font-size: 0.625rem;
					font-weight: 700;
					padding: 3px 7px;
					border-radius: 6px;
				}
				.event-price-physical {
					background: rgba(246,133,33,0.1) !important;
					color: #F68521 !important;
				}

				/* Content */
				.event-title { font-size: 1rem; font-weight: 700; color: #1e293b; line-height: 1.4; }
				.event-description {
					font-size: 0.8125rem;
					line-height: 1.55;
					display: -webkit-box;
					-webkit-line-clamp: 2;
					-webkit-box-orient: vertical;
					overflow: hidden;
				}
				.event-speakers { display: flex; flex-direction: column; gap: 0.25rem; }
				.event-speaker { font-size: 0.8125rem; color: #475569; display: flex; align-items: center; }
				.speaker-name { font-weight: 600; }
				.speaker-title { color: #94a3b8; }
				.event-meta-list { display: flex; flex-direction: column; gap: 0.4rem; }
				.event-meta-item { font-size: 0.8125rem; color: #475569; display: flex; align-items: center; }

				/* CTA */
				.event-details-btn {
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
					color: white; font-weight: 600; font-size: 0.875rem;
					padding: 0.6rem 1rem; border-radius: 10px;
					text-decoration: none; transition: opacity 0.2s;
				}
				.event-details-btn:hover { opacity: 0.9; color: white; }
				.event-registered-badge,
				.event-full-badge,
				.event-ended-badge {
					display: flex; align-items: center; justify-content: center;
					font-weight: 700; font-size: 0.8125rem;
					padding: 0.4rem 1rem; border-radius: 8px;
				}
				.event-registered-badge {
					background: rgba(16,185,129,0.1); color: #10b981;
					border: 1.5px solid rgba(16,185,129,0.3);
				}
				.event-full-badge {
					background: rgba(239,68,68,0.08); color: #ef4444;
					border: 1.5px solid rgba(239,68,68,0.2);
				}
				.event-ended-badge {
					background: rgba(148,163,184,0.1); color: #94a3b8;
					border: 1.5px solid rgba(148,163,184,0.2);
				}
			`}</style>
		</Card>
	);
};

export default EventCard;
